import { openThreadReplicaDb, requestToPromise, transactionToPromise } from "@/lib/thread-replica-idb"
import {
  FIRST_THREAD_REPLICA_SEQUENCE,
  THREAD_REPLICA_EVENTS_REPLAY_IDENTITY_INDEX,
  THREAD_REPLICA_EVENTS_STORE,
  type ThreadReplicaBaselineEventInput,
  type ThreadReplicaBaselineEventRow,
  type ThreadReplicaEventLane,
  type ThreadReplicaEventRow,
  type ThreadReplicaRuntimeEventInput,
  type ThreadReplicaRuntimeEventRow,
  type ThreadReplicaScope,
  normalizePositiveInteger,
  normalizeThreadReplicaBaselineEventRow,
  normalizeThreadReplicaEventRow,
  normalizeThreadReplicaRuntimeEventRow,
} from "@/lib/thread-replica-types"
import type { ThreadSyncReplicaSourceKind } from "@/lib/thread-sync-replica-provenance"

function buildThreadReplicaEventKey(args: {
  scopeKey: string
  sessionId: string
  lane: ThreadReplicaEventLane
  sequence: number
}): [string, string, ThreadReplicaEventLane, number] {
  return [args.scopeKey, args.sessionId, args.lane, args.sequence]
}

function buildThreadReplicaReplayIdentityKey(args: {
  scopeKey: string
  sessionId: string
  lane: ThreadReplicaEventLane
  replayEventId: string
}): [string, string, ThreadReplicaEventLane, string] {
  return [args.scopeKey, args.sessionId, args.lane, args.replayEventId]
}

/**
 * Large replicas can materialize hundreds of MB if hydration reads an entire
 * lane in one go. Keep each IndexedDB page small enough to bound temporary
 * arrays while still amortizing request overhead. If individual event payloads
 * grow materially, we may need to switch this to byte-based paging instead.
 */
const THREAD_REPLICA_READ_BATCH_EVENT_COUNT = 128

export function buildThreadReplicaRuntimeEventRow(args: {
  scopeKey: string
  sessionId: string
  replicaEpoch: number
  event: ThreadReplicaRuntimeEventInput
  sourceKind: ThreadSyncReplicaSourceKind | null
}): ThreadReplicaRuntimeEventRow {
  // Persist the conversation tag that the transport attached to this row so
  // hydration can rebuild a per-conversation projection from the shared event
  // lane. Missing/empty ids become null on disk and normalize to "default" when
  // read, matching the legacy single-chat behaviour.
  const normalizedConversationId =
    typeof args.event.conversationId === "string" && args.event.conversationId.trim().length > 0
      ? args.event.conversationId.trim()
      : null
  return {
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: args.replicaEpoch,
    contractVersion: args.event.contractVersion,
    replayEventId: args.event.replayEventId,
    lane: "runtime",
    sequence: args.event.id,
    eventType: args.event.type,
    payload: args.event.payload,
    timestamp: args.event.timestamp,
    childSessionId: args.event.childSessionId?.trim() || null,
    conversationId: normalizedConversationId,
    sourceKind: args.sourceKind,
  }
}

export function buildThreadReplicaBaselineEventRow(args: {
  scopeKey: string
  sessionId: string
  replicaEpoch: number
  event: ThreadReplicaBaselineEventInput
  sourceKind: ThreadSyncReplicaSourceKind | null
}): ThreadReplicaBaselineEventRow {
  return {
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: args.replicaEpoch,
    contractVersion: args.event.contractVersion,
    replayEventId: args.event.replayEventId,
    lane: "baseline",
    sequence: args.event.baselineSeq,
    eventType: args.event.type,
    payload: args.event.payload,
    timestamp: args.event.timestamp,
    originSessionId: args.event.originSessionId?.trim() || null,
    originEventId: normalizePositiveInteger(args.event.originEventId) ?? null,
    sourceKind: args.sourceKind,
  }
}

function serializePayload(value: unknown): string | null {
  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

export function areThreadReplicaEventRowsEquivalent(args: {
  left: ThreadReplicaEventRow
  right: ThreadReplicaEventRow
}): boolean {
  if (
    args.left.lane !== args.right.lane ||
    args.left.sequence !== args.right.sequence ||
    args.left.contractVersion !== args.right.contractVersion ||
    args.left.replayEventId !== args.right.replayEventId ||
    args.left.eventType !== args.right.eventType ||
    args.left.timestamp !== args.right.timestamp ||
    serializePayload(args.left.payload) !== serializePayload(args.right.payload)
  ) {
    return false
  }

  if (args.left.lane === "runtime" && args.right.lane === "runtime") {
    // Two replay-identity rows are only equivalent when the conversation tag
    // matches as well. Without this the browser could silently accept a replay
    // of the same raw event id under a different conversation, which would
    // corrupt the per-conversation projection built by hydrate/filter logic.
    return (
      args.left.childSessionId === args.right.childSessionId &&
      (args.left.conversationId ?? null) === (args.right.conversationId ?? null)
    )
  }

  if (args.left.lane === "baseline" && args.right.lane === "baseline") {
    return (
      args.left.originSessionId === args.right.originSessionId &&
      args.left.originEventId === args.right.originEventId
    )
  }

  return false
}

export async function readThreadReplicaLaneEvents(args: ThreadReplicaScope & {
  lane: ThreadReplicaEventLane
  fromSequence?: number
  toSequence?: number
}): Promise<ThreadReplicaEventRow[]> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return []
  }

  const fromSequence = Math.max(
    FIRST_THREAD_REPLICA_SEQUENCE,
    normalizePositiveInteger(args.fromSequence) ?? FIRST_THREAD_REPLICA_SEQUENCE,
  )
  const toSequence = Math.max(fromSequence, normalizePositiveInteger(args.toSequence) ?? Number.MAX_SAFE_INTEGER)
  const transaction = db.transaction(THREAD_REPLICA_EVENTS_STORE, "readonly")
  const keyRange = IDBKeyRange.bound(
    buildThreadReplicaEventKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: args.lane,
      sequence: fromSequence,
    }),
    buildThreadReplicaEventKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: args.lane,
      sequence: toSequence,
    }),
  )
  const values = await requestToPromise(
    transaction.objectStore(THREAD_REPLICA_EVENTS_STORE).getAll(keyRange),
  )
  await transactionToPromise(transaction)
  return values
    .map((value) => normalizeThreadReplicaEventRow(value))
    .filter((value): value is ThreadReplicaEventRow => value !== null)
}

export async function readThreadReplicaLaneEventBatches(args: ThreadReplicaScope & {
  lane: ThreadReplicaEventLane
  fromSequence?: number
  toSequence?: number
  onBatch: (rows: ThreadReplicaEventRow[]) => void
}): Promise<{
  count: number
  isContiguous: boolean
}> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return { count: 0, isContiguous: true }
  }

  const fromSequence = Math.max(
    FIRST_THREAD_REPLICA_SEQUENCE,
    normalizePositiveInteger(args.fromSequence) ?? FIRST_THREAD_REPLICA_SEQUENCE,
  )
  const toSequence = Math.max(fromSequence, normalizePositiveInteger(args.toSequence) ?? Number.MAX_SAFE_INTEGER)
  const transaction = db.transaction(THREAD_REPLICA_EVENTS_STORE, "readonly")
  const store = transaction.objectStore(THREAD_REPLICA_EVENTS_STORE)
  let nextSequence = fromSequence
  let count = 0
  let isContiguous = true

  while (nextSequence <= toSequence) {
    const values = await requestToPromise(
      store.getAll(
        buildThreadReplicaEventSequenceRange({
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          lane: args.lane,
          fromSequence: nextSequence,
          toSequence,
        }),
        THREAD_REPLICA_READ_BATCH_EVENT_COUNT,
      ),
    )

    if (values.length === 0) {
      break
    }

    const rows: ThreadReplicaEventRow[] = []
    for (const value of values) {
      const row = normalizeThreadReplicaEventRow(value)
      if (!row || row.sequence !== nextSequence) {
        isContiguous = false
        break
      }

      rows.push(row)
      count += 1
      nextSequence = row.sequence + 1
    }

    if (rows.length > 0) {
      args.onBatch(rows)
    }

    if (!isContiguous || values.length < THREAD_REPLICA_READ_BATCH_EVENT_COUNT) {
      break
    }
  }

  await transactionToPromise(transaction)
  return { count, isContiguous }
}

export async function readThreadReplicaRuntimeEvent(args: ThreadReplicaScope & {
  sequence: number
}): Promise<ThreadReplicaRuntimeEventRow | null> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return null
  }

  const transaction = db.transaction(THREAD_REPLICA_EVENTS_STORE, "readonly")
  const value = await requestToPromise(
    transaction.objectStore(THREAD_REPLICA_EVENTS_STORE).get(
      buildThreadReplicaEventKey({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        lane: "runtime",
        sequence: args.sequence,
      }),
    ),
  )
  await transactionToPromise(transaction)
  return normalizeThreadReplicaRuntimeEventRow(value)
}

export async function readThreadReplicaBaselineEvent(args: ThreadReplicaScope & {
  sequence: number
}): Promise<ThreadReplicaBaselineEventRow | null> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return null
  }

  const transaction = db.transaction(THREAD_REPLICA_EVENTS_STORE, "readonly")
  const value = await requestToPromise(
    transaction.objectStore(THREAD_REPLICA_EVENTS_STORE).get(
      buildThreadReplicaEventKey({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        lane: "baseline",
        sequence: args.sequence,
      }),
    ),
  )
  await transactionToPromise(transaction)
  return normalizeThreadReplicaBaselineEventRow(value)
}

export async function clearThreadReplicaEvents(args: ThreadReplicaScope): Promise<void> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return
  }

  const transaction = db.transaction(THREAD_REPLICA_EVENTS_STORE, "readwrite")
  const store = transaction.objectStore(THREAD_REPLICA_EVENTS_STORE)
  store.delete(
    buildThreadReplicaEventSequenceRange({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: "runtime",
      fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
      toSequence: Number.MAX_SAFE_INTEGER,
    }),
  )
  store.delete(
    buildThreadReplicaEventSequenceRange({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: "baseline",
      fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
      toSequence: Number.MAX_SAFE_INTEGER,
    }),
  )

  await transactionToPromise(transaction)
}

export function buildThreadReplicaEventSequenceRange(args: ThreadReplicaScope & {
  lane: ThreadReplicaEventLane
  fromSequence: number
  toSequence: number
}): IDBKeyRange {
  return IDBKeyRange.bound(
    buildThreadReplicaEventKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: args.lane,
      sequence: args.fromSequence,
    }),
    buildThreadReplicaEventKey({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: args.lane,
      sequence: args.toSequence,
    }),
  )
}

export function buildThreadReplicaReplayIdentityLookupKey(args: {
  scopeKey: string
  sessionId: string
  lane: ThreadReplicaEventLane
  replayEventId: string
}): [string, string, ThreadReplicaEventLane, string] {
  return buildThreadReplicaReplayIdentityKey(args)
}

export function getThreadReplicaEventsStoreName(): string {
  return THREAD_REPLICA_EVENTS_STORE
}

export function getThreadReplicaReplayIdentityIndexName(): string {
  return THREAD_REPLICA_EVENTS_REPLAY_IDENTITY_INDEX
}
